/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetRuoliLiteParams,
  GetRuoliParams,
  Ruolo,
  RuoloExt,
  WsRispostaPaginataListRuoloExt,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Cerca uno o più ruoli utente. Ordinabile per descrizione, denominazione, id
 * @summary Cerca uno o più ruoli utente. Ordinabile per descrizione, denominazione, id
 */
export const getRuoli = (
  params?: GetRuoliParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListRuoloExt>(
    { url: `/framework-cfg-utenti/ruoli`, method: 'get', params, signal },
    options,
  );
};

export const getGetRuoliQueryKey = (params?: GetRuoliParams) =>
  [`/framework-cfg-utenti/ruoli`, ...(params ? [params] : [])] as const;

export const getGetRuoliQueryOptions = <
  TData = Awaited<ReturnType<typeof getRuoli>>,
  TError = ErrorType<unknown>,
>(
  params?: GetRuoliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRuoli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getRuoli>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRuoliQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRuoli>>> = ({
    signal,
  }) => getRuoli(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetRuoliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRuoli>>
>;
export type GetRuoliQueryError = ErrorType<unknown>;

/**
 * @summary Cerca uno o più ruoli utente. Ordinabile per descrizione, denominazione, id
 */
export const useGetRuoli = <
  TData = Awaited<ReturnType<typeof getRuoli>>,
  TError = ErrorType<unknown>,
>(
  params?: GetRuoliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRuoli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetRuoliQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un ruolo utente
 * @summary Aggiorna un ruolo utente
 */
export const putRuoli = (
  ruolo: Ruolo,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/ruoli`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: ruolo,
    },
    options,
  );
};

export const getPutRuoliMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putRuoli>>,
    TError,
    { data: Ruolo },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putRuoli>>,
  TError,
  { data: Ruolo },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putRuoli>>,
    { data: Ruolo }
  > = props => {
    const { data } = props ?? {};

    return putRuoli(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutRuoliMutationResult = NonNullable<
  Awaited<ReturnType<typeof putRuoli>>
>;
export type PutRuoliMutationBody = Ruolo;
export type PutRuoliMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna un ruolo utente
 */
export const usePutRuoli = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putRuoli>>,
    TError,
    { data: Ruolo },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutRuoliMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Inserisce un ruolo utente
 * @summary Inserisce un ruolo utente
 */
export const postRuoli = (
  ruolo: Ruolo,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/ruoli`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: ruolo,
    },
    options,
  );
};

export const getPostRuoliMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postRuoli>>,
    TError,
    { data: Ruolo },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postRuoli>>,
  TError,
  { data: Ruolo },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postRuoli>>,
    { data: Ruolo }
  > = props => {
    const { data } = props ?? {};

    return postRuoli(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostRuoliMutationResult = NonNullable<
  Awaited<ReturnType<typeof postRuoli>>
>;
export type PostRuoliMutationBody = Ruolo;
export type PostRuoliMutationError = ErrorType<unknown>;

/**
 * @summary Inserisce un ruolo utente
 */
export const usePostRuoli = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postRuoli>>,
    TError,
    { data: Ruolo },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostRuoliMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce ruolo a partire da id
 * @summary Restituisce ruolo a partire da id
 */
export const getRuoliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RuoloExt>(
    { url: `/framework-cfg-utenti/ruoli/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetRuoliIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/ruoli/${id}`] as const;

export const getGetRuoliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getRuoliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRuoliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getRuoliId>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRuoliIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRuoliId>>> = ({
    signal,
  }) => getRuoliId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetRuoliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRuoliId>>
>;
export type GetRuoliIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce ruolo a partire da id
 */
export const useGetRuoliId = <
  TData = Awaited<ReturnType<typeof getRuoliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRuoliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetRuoliIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cancella un ruolo utente
 * @summary Cancella un ruolo utente
 */
export const deleteRuoliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/ruoli/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteRuoliIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteRuoliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteRuoliId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteRuoliId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteRuoliId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteRuoliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteRuoliId>>
>;

export type DeleteRuoliIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella un ruolo utente
 */
export const useDeleteRuoliId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteRuoliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteRuoliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API efficiente per cercare uno o più ruoli utente. Ordinabile e Filtrabile per denominazione
 * @summary Cerca uno o più ruoli utente. Ordinabile e Filtrabile per denominazione
 */
export const getRuoliLite = (
  params?: GetRuoliLiteParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListRuoloExt>(
    { url: `/framework-cfg-utenti/ruoli/lite`, method: 'get', params, signal },
    options,
  );
};

export const getGetRuoliLiteQueryKey = (params?: GetRuoliLiteParams) =>
  [`/framework-cfg-utenti/ruoli/lite`, ...(params ? [params] : [])] as const;

export const getGetRuoliLiteQueryOptions = <
  TData = Awaited<ReturnType<typeof getRuoliLite>>,
  TError = ErrorType<unknown>,
>(
  params?: GetRuoliLiteParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRuoliLite>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getRuoliLite>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRuoliLiteQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRuoliLite>>> = ({
    signal,
  }) => getRuoliLite(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetRuoliLiteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRuoliLite>>
>;
export type GetRuoliLiteQueryError = ErrorType<unknown>;

/**
 * @summary Cerca uno o più ruoli utente. Ordinabile e Filtrabile per denominazione
 */
export const useGetRuoliLite = <
  TData = Awaited<ReturnType<typeof getRuoliLite>>,
  TError = ErrorType<unknown>,
>(
  params?: GetRuoliLiteParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRuoliLite>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetRuoliLiteQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
